import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import InputMask from 'react-input-mask';
import { phoneFormats } from '../constants/phoneFormats';
import { uploadVisuelPass } from '../api/formApi';
import {
	PJ_TYPES,
	PAYMENT_GATEWAYS,
	USER_DATA_FIELDS,
	FORMULAIRE_OTIPASS_FEATURES,
	BOUTIQUE_OTIPASS_FEATURES,
	FEATURE_FORM_PJ,
	FEATURE_BOUTIQUE_TARIF,
	FEATURE_PAIEMENT_CB,
	champsFormula,
	FINANCE_PERIODICITY_OPTIONS,
	FINANCE_CURRENCY_ISO_OPTIONS,
} from '../constants/formCatalog';
import { defaultTelMask } from '../constants/contactPlaceholders';
import { STEP_SUCCESS } from '../constants/wizard';

export function StepProject({ data = {}, onChange, errors = {}, onBlur }) {
	const { t } = useTranslation();
	const projectType = data.project_type ?? 'ot';
	return (
		<div className="section">
			<div className="section-title">{t('steps.stepProject.title')}</div>
			<label htmlFor="Type de plateforme *">
				{t('steps.stepProject.platformType')}
			</label>
			<select
				id="project_type"
				name="project_type"
				value={projectType}
				onChange={onChange}
			>
				<option value="ot">{t('steps.stepProject.tourism')}</option>
				<option value="pms">{t('steps.stepProject.pms')}</option>
			</select>
			<label htmlFor="nom_projet">{t('steps.stepProject.nomProjet')}</label>
			<input
				type="text"
				id="nom_projet"
				name="nom_projet"
				placeholder={t('steps.stepProject.phNom')}
				value={data.nom_projet || ''}
				onChange={onChange}
				onBlur={onBlur}
				className={errors.nom_projet ? 'error' : ''}
			/>
			<small className="help-text">{t('steps.stepProject.helpNomProjet')}</small>
			{errors.nom_projet && <span className="error-message">{errors.nom_projet}</span>}
			<label htmlFor="nom_pass">{t('steps.stepProject.nomPass')}</label>
			<input
				type="text"
				id="nom_pass"
				name="nom_pass"
				placeholder={t('steps.stepProject.phPass')}
				value={data.nom_pass || ''}
				onChange={onChange}
				onBlur={onBlur}
				className={errors.nom_pass ? 'error' : ''}
			/>
			<small className="help-text">{t('steps.stepProject.helpNomPass')}</small>
				{errors.nom_pass && <span className="error-message">{errors.nom_pass}</span>}
				<label htmlFor="territoire">{t('steps.stepProject.territoire')}</label>
				<input
					type="text"
					id="territoire"
					name="territoire"
					placeholder={t('steps.stepProject.phTerr')}
					value={data.territoire || ''}
					onChange={onChange}
					onBlur={onBlur}
					className={errors.territoire ? 'error' : ''}
				/>
			<small className="help-text">{t('steps.stepProject.helpTerritoire')}</small>
				{errors.territoire && <span className="error-message">{errors.territoire}</span>}
		</div>
	);
}

export function StepIssuer({ data = {}, onChange, errors = {}, onBlur, setErrors }) {
	const { t } = useTranslation();
	const trCountry = (country) => t(`catalog.countries.${country}`, { defaultValue: country });
	const isPaysAutre = data.pays === 'Autre';
	const { mask, placeholder } = useMemo(() => {
		return phoneFormats[data.pays] || phoneFormats["France"];
	}, [data.pays]);	
	return (
		<div className="section">
		<div className="section-title">{t('steps.stepIssuer.title')}</div>
		<label htmlFor="nom_emetteur">{t('steps.stepIssuer.nom')}</label>
		<input
			type="text"
			id="nom_emetteur"
			name="nom_emetteur"
			placeholder="Exemple : Sète agglopôle méditerranée"
			value={data.nom_emetteur || ''}
			onChange={onChange}
			onBlur={onBlur}
			className={errors.nom_emetteur ? 'error' : ''}
		/>
		<small className="help-text">{t('steps.stepIssuer.helpNom')}</small>
		{errors.nom_emetteur && <span className="error-message">{errors.nom_emetteur}</span>}
		<label htmlFor="reference_emetteur">{t('steps.stepIssuer.ref')}</label>
		<input
			type="text"
			id="reference_emetteur"
			name="reference_emetteur"
			placeholder="Exemple : SAM"
			value={data.reference_emetteur || ''}
			onChange={onChange}
			onBlur={onBlur}
			className={errors.reference_emetteur ? 'error uppercase' : 'uppercase'}
		/>
		<small className="help-text">{t('steps.stepIssuer.helpRef')}</small>
		{errors.reference_emetteur && <span className="error-message">{errors.reference_emetteur}</span>}
		<label htmlFor="adresse_postale">{t('steps.stepIssuer.adresse')}</label>
		<input
			type="text"
			id="adresse_postale"
			name="adresse_postale"
			placeholder="Exemple : 4 avenue d'Aigues, BP 600"
			value={data.adresse_postale || ''}
			onChange={onChange}
			onBlur={onBlur}
			className={errors.adresse_postale ? 'error' : ''}
		/>
		<small className="help-text">{t('steps.stepIssuer.helpAdresse')}</small>
		{errors.adresse_postale && <span className="error-message">{errors.adresse_postale}</span>}
		<label htmlFor="code_postal">{t('steps.stepIssuer.cp')}</label>
		<input
			type="text"
			id="code_postal"
			name="code_postal"
			placeholder="Exemple : 34110"
			value={data.code_postal || ''}
			onChange={onChange}
			onBlur={onBlur}
			className={errors.code_postal ? 'error' : ''}
		/>
		{errors.code_postal && <span className="error-message">{errors.code_postal}</span>}
		<label htmlFor="ville">{t('steps.stepIssuer.ville')}</label>
		<input
			type="text"
			id="ville"
			name="ville"
			placeholder="Exemple : Frontignan"
			value={data.ville || ''}
			onChange={onChange}
			onBlur={onBlur}
			className={errors.ville ? 'error' : ''}
		/>
		{errors.ville && <span className="error-message">{errors.ville}</span>}
		<label htmlFor="pays">{t('steps.stepIssuer.pays')}</label>
		<select id="pays" name="pays" value={data.pays || "France"} onChange={onChange}>
			<optgroup label={t('app.common.europe')}>
				{Object.keys(phoneFormats).map((country) => (
					<option key={country} value={country}>{trCountry(country)}</option>
				))}
			</optgroup>
			<option value="Autre" style={{ fontWeight: "bold" }}>{t('app.common.other')}</option>
		</select>
		{data.pays === 'Autre' && (
		<div>
			<input
			type="text"
			id="pays_autre"
			name="pays_autre"
			placeholder={t('steps.stepIssuer.paysAutrePh')}
			value={data.pays_autre || ''}
			onChange={onChange}
			disabled={!isPaysAutre}
			/>
			{errors.pays_autre && <span className="error-message">{errors.pays_autre}</span>}
		</div>
		)}
		{errors.pays && <span className="error-message">{errors.pays}</span>}
		<label htmlFor="telephone">{t('steps.stepIssuer.tel')}</label>
		<InputMask
			mask={mask}
			value={data.telephone || ""}
			onChange={onChange}
		>
			{(inputProps) => (
				<input
					type="tel"
					id="telephone"
					name="telephone"
					placeholder={placeholder}
					{...inputProps}
				/>
			)}
		</InputMask>
		<small className="help-text">{t('steps.stepIssuer.helpTel')}</small>
		<label htmlFor="site_public">{t('steps.stepIssuer.site')}</label>
		<input
			type="url"
			id="site_public"
			name="site_public"
			placeholder="Exemple : https://www.agglopole.fr/"
			value={data.site_public || ''}
			onChange={onChange}
			onBlur={onBlur}
			className={errors.site_public ? 'error' : ''}
		/>
		<small className="help-text">{t('steps.stepIssuer.helpSite')}</small>
		{errors.site_public && <span className="error-message">{errors.site_public}</span>}
		<label htmlFor="domaine">{t('steps.stepIssuer.domaine')}</label>
		<input
			type="text"
			id="domaine"
			name="domaine"
			placeholder="Exemple : agglopole.fr"
			value={data.domaine || ''}
			onChange={onChange}
			onBlur={onBlur}
			className={errors.domaine ? 'error' : ''}
		/>
		{errors.domaine && <span className="error-message">{errors.domaine}</span>}
		<div className="subsection-title">{t('steps.stepIssuer.emailBlock')}</div>
		<label htmlFor="email_public">{t('steps.stepIssuer.emailPublic')}</label>
		<input
			type="email"
			id="email_public"
			name="email_public"
			placeholder="Exemple : accueil@agglopole.fr"
			value={data.email_public || ''}
			onChange={onChange}
			onBlur={onBlur}
			className={errors.email_public ? 'error' : ''}
		/>
		{errors.email_public && <span className="error-message">{errors.email_public}</span>}
		<label htmlFor="email_reception">{t('steps.stepIssuer.emailNotif')}</label>
		<input
			type="email"
			id="email_reception"
			name="email_reception"
			placeholder="Exemple : accueil@agglopole.fr"
			value={data.email_reception || ''}
			onChange={onChange}
			onBlur={onBlur}
			className={errors.email_reception ? 'error' : ''}
		/>
		{errors.email_reception && <span className="error-message">{errors.email_reception}</span>}
		<div className="subsection-title">{t('steps.stepIssuer.socialBlock')}</div>
		<label htmlFor="facebook">{t('steps.stepIssuer.facebook')}</label>
		<input
			type="url"
			id="facebook"
			name="facebook"
			placeholder="Exemple : https://www.facebook.com/seteagglo"
			value={data.facebook || ''}
			onChange={onChange}
			onBlur={onBlur}
			className={errors.facebook ? 'error' : ''}
		/>
		{errors.facebook && <span className="error-message">{errors.facebook}</span>}
		<label htmlFor="youtube">{t('steps.stepIssuer.youtube')}</label>
		<input
			type="url"
			id="youtube"
			name="youtube"
			placeholder="Exemple : https://www.youtube.com/user/seteagglo"
			value={data.youtube || ''}
			onChange={onChange}
			onBlur={onBlur}
			className={errors.youtube ? 'error' : ''}
		/>
		{errors.youtube && <span className="error-message">{errors.youtube}</span>}
		<label htmlFor="instagram">{t('steps.stepIssuer.instagram')}</label>
		<input
			type="url"
			id="instagram"
			name="instagram"
			placeholder="Exemple : https://www.instagram.com/sete_agglo/"
			value={data.instagram || ''}
			onChange={onChange}
			onBlur={onBlur}
			className={errors.instagram ? 'error' : ''}
		/>
		{errors.instagram && <span className="error-message">{errors.instagram}</span>}
		<label htmlFor="x">{t('steps.stepIssuer.x')}</label>
		<input
			type="url"
			id="x"
			name="x"
			placeholder="Exemple : https://x.com/SeteAgglo"
			value={data.x || ''}
			onChange={onChange}
			onBlur={onBlur}
			className={errors.x ? 'error' : ''}
		/>
		{errors.x && <span className="error-message">{errors.x}</span>}
		</div>
	);
}

export function StepServices({ data = {}, onChange, errors = {}, setErrors }) {
	const { t } = useTranslation();
	const isTransport = data.is_transport === 'oui';
	return data.project_type === 'pms' ? (
		<div className="section">
			<div className="section-title">{t('steps.services.title')}</div>
			<div className="checkbox-group">
				<label>
					<input
						type="checkbox"
						name="service_dechetterie"
						checked={data.service_dechetterie || false}
						onChange={onChange}
					/>
					{t('steps.services.dechetterie')}
				</label>
				<label>
					<input
						type="checkbox"
						name="service_piscine"
						checked={data.service_piscine || false}
						onChange={onChange}
					/>
					{t('steps.services.piscine')}
				</label>
				<label>
					<input
						type="checkbox"
						name="service_mediatheque"
						checked={data.service_mediatheque || false}
						onChange={onChange}
					/>
					{t('steps.services.mediatheque')}
				</label>
				<label>
					<input
						type="checkbox"
						name="service_musee"
						checked={data.service_musee || false}
						onChange={onChange}
					/>
					{t('steps.services.musee')}
				</label>
				<label>
					<input
						type="checkbox"
						name="service_transport"
						checked={data.service_transport || false}
						onChange={onChange}
					/>
					{t('steps.services.transport')}
				</label>
			</div>
			<label htmlFor="service_autres">{t('steps.services.autres')}</label>
			<input
				type="text"
				id="service_autres"
				name="service_autres"
				placeholder={t('steps.services.phService')}
				value={data.service_autres || ''}
				onChange={onChange}
			/>
		</div>
	) :
	(
		<div className="section">
			<div className="section-title">{t('steps.services.title')}</div>
			<label htmlFor="is_transport">
				{t('steps.services.transportOffer')}
			</label>
			<select
				id="is_transport"
				name="is_transport"
				value={data.is_transport || 'non'}
				onChange={onChange}
			>
				<option value="oui">{t('app.common.yes')}</option>
				<option value="non">{t('app.common.no')}</option>
			</select>
			{isTransport && (
				<>
					<label htmlFor="transport_operator">
						{t('steps.services.operator')}
					</label>
					<select
						id="transport_operator"
						name="transport_operator"
						value={data.transport_operator ?? 'Actoll'}
						onChange={onChange}
					>
						<option value="Actoll">Actoll</option>
						<option value="Airweb">Airweb</option>
						<option value="Atsuke">Atsuke</option>
						<option value="Frimobil">Frimobil</option>
						<option value="HSL">HSL</option>
						<option value="M-Tag">M-Tag</option>
						<option value="Otipass Transport">Otipass Transport</option>
						<option value="SNCF">SNCF</option>
						<option value="Autre">{t('app.common.other')}</option>
					</select>
					{data.transport_operator === 'Autre' && (
						<>
							<input
								type="text"
								id="transport_operator_other"
								name="transport_operator_other"
								placeholder={t('steps.services.operatorPh')}
								value={data.transport_operator_other || ''}
								onChange={onChange}
								className={isTransport && !data.transport_operator_other ? 'error' : ''}
							/>
							{errors.transport_operator_other && <span className="error-message">{errors.transport_operator_other}</span>}
						</>
					)}
					
					<label htmlFor="is_transport_duration">
						{t('steps.services.durationQ')}
					</label>
					<select
						id="is_transport_duration"
						name="is_transport_duration"
						value={data.is_transport_duration ?? 'non'}
						onChange={onChange}
					>
						<option value="oui">{t('app.common.yes')}</option>
						<option value="non">{t('app.common.no')}</option>
					</select>
				</>
			)}
		</div>
	);
}

export function StepTouristData({ data = {}, onChange, errors = {}, setErrors, validateStep }) {
	const { t } = useTranslation();
	const isAlimentationOui = data.alimentation_base_touristique === 'oui';
	const handleAlimentationChange = (e) => {
		onChange(e);
		validateStep(4);
	};

	return (
		<div className="section">
			<div className="section-title">{t('steps.tourist.title')}</div>
			<label htmlFor="alimentation_base_touristique">
				{t('steps.tourist.alim')}
			</label>
			<select
				id="alimentation_base_touristique"
				name="alimentation_base_touristique"
				value={data.alimentation_base_touristique || 'non'}
				onChange={handleAlimentationChange}
			>
				<option value="oui">{t('app.common.yes')}</option>
				<option value="non">{t('app.common.no')}</option>
			</select>
			{data.alimentation_base_touristique === 'oui' && (
				<>
					<label htmlFor="editeur_plateforme">{t('steps.tourist.editor')}</label>
					<select
						id="editeur_plateforme"
						name="editeur_plateforme"
						value={data.editeur_plateforme ?? 'Apidae'}
						onChange={onChange}
						disabled={!isAlimentationOui}
					>
						<option value="Apidae">Apidae</option>
						<option value="Tourinsoft">Tourinsoft</option>
						<option value="Autre">{t('app.common.other')}</option>
					</select>
					{data.editeur_plateforme === 'Autre' && (
						<>
							<input
								type="text"
								id="editeur_plateforme_autre"
								name="editeur_plateforme_autre"
								placeholder={t('steps.tourist.editorPh')}
								value={data.editeur_plateforme_autre || ''}
								onChange={onChange}
								disabled={!isAlimentationOui}
								className={isAlimentationOui && !data.editeur_plateforme_autre ? 'error' : ''}
							/>
							{errors.editeur_plateforme_autre && <span className="error-message">{errors.editeur_plateforme_autre}</span>}
						</>
					)}

					<label htmlFor="frequence_synchronisations">{t('steps.tourist.freq')}</label>
					<select
						id="frequence_synchronisations"
						name="frequence_synchronisations"
						value={data.frequence_synchronisations ?? 'Synchro initiale'}
						onChange={onChange}
						disabled={!isAlimentationOui}
					>
						<option value="Synchro initiale">{t('catalog.frequence.synchroInit')}</option>
						<option value="Synchro périodique">{t('catalog.frequence.synchroMens')}</option>
						<option value="Autre">{t('app.common.other')}</option>
					</select>
					{data.frequence_synchronisations === 'Autre' && (
						<>
							<input
								type="text"
								id="frequence_synchronisations_autre"
								name="frequence_synchronisations_autre"
								placeholder={t('steps.tourist.freqPh')}
								value={data.frequence_synchronisations_autre || ''}
								onChange={onChange}
								disabled={!isAlimentationOui}
								className={isAlimentationOui && !data.frequence_synchronisations_autre ? 'error' : ''}
							/>
							{errors.frequence_synchronisations_autre && <span className="error-message">{errors.frequence_synchronisations_autre}</span>}
						</>
					)}
				</>
			)}
		</div>
	);
}

export function StepSupport({ data = {}, onChange, errors = {}, setErrors, validateStep }) {
	const { t } = useTranslation();
	const isSupportPhysiqueOui = data.support_physique === 'oui';
	const [fileName, setFileName] = useState('');

	const handleSupportPhysiqueChange = (e) => {
		onChange(e);
		if (e.target.value === 'non') {
			const newErrors = { ...errors };
			delete newErrors.identifiant_1;
			delete newErrors.identifiant_2;
			setErrors(newErrors);
			if (validateStep) validateStep(5);
		}
	};

	const handleFileChange = async (e) => {
		const file = e.target.files[0];
		if (file) {
			setFileName(file.name);
			try {
				const result = await uploadVisuelPass(file);
				onChange({ target: { name: 'visuel_pass_recto', value: result.filePath } });
			} catch (error) {
				console.error("Erreur lors de l'upload du fichier :", error);
			}
		}
	};

	return (
		<div className="section">
			<div className="section-title">{t('steps.support.visuelTitle')}</div>
			<label htmlFor="visuel_pass_recto">{t('steps.support.visuelQ')}</label>
			<div className="file-upload-wrapper">
				<input
					type="file"
					id="visuel_pass_recto"
					name="visuel_pass_recto"
					accept="image/*,.pdf"
					onChange={handleFileChange}
					className="file-upload-input"
				/>
				<label htmlFor="visuel_pass_recto" className="file-upload-label">
					{t('steps.support.chooseFile')}
				</label>
				{fileName && <p className="file-name">{t('steps.support.fileSelected')}{' '}{fileName}</p>}
				{data.visuel_pass_recto && (
					<div>
						<img
							src={data.visuel_pass_recto}
							alt={t('steps.support.altVisuel')}
							className="file-preview"
						/>
					</div>
				)}
			</div>

			<div className="section-title">{t('steps.support.supDematTitle')}</div>
			<label htmlFor="support_dematerialise">{t('steps.support.supDematQ')}</label>
			<select
				id="support_dematerialise"
				name="support_dematerialise"
				value={data.support_dematerialise || 'oui'}
				onChange={onChange}
			>
				<option value="oui">{t('app.common.yes')}</option>
				<option value="non">{t('app.common.no')}</option>
			</select>

			<div className="section-title">{t('steps.support.supPhysTitle')}</div>
			<label htmlFor="support_physique">{t('steps.support.supPhysQ')}</label>
			<select
				id="support_physique"
				name="support_physique"
				value={data.support_physique || 'non'}
				onChange={handleSupportPhysiqueChange}
			>
				<option value="oui">{t('app.common.yes')}</option>
				<option value="non">{t('app.common.no')}</option>
			</select>
			{isSupportPhysiqueOui && (
				<>
					<label htmlFor="identifiant_1">{t('steps.support.id1')}</label>
					<select
						id="identifiant_1"
						name="identifiant_1"
						value={data.identifiant_1 || 'aucun'}
						onChange={onChange}
						disabled={!isSupportPhysiqueOui}
					>
						{['aucun', 'qrcode', 'clair', 'unique', 'serie', 'calypso', 'amc', 'autre'].map((v) => (
							<option key={v} value={v}>
								{t(`catalog.identifiant.${v}`)}
							</option>
						))}
					</select>
					{data.identifiant_1 === 'autre' && (
						<>
							<label htmlFor="identifiant_1_autre">{t('steps.support.id1prec')}</label>
							<input
								type="text"
								id="identifiant_1_autre"
								name="identifiant_1_autre"
								placeholder={t('steps.support.id1ph')}
								value={data.identifiant_1_autre || ''}
								onChange={onChange}
								disabled={!isSupportPhysiqueOui}
								className={isSupportPhysiqueOui && !data.identifiant_1_autre ? 'error' : ''}
							/>
							{errors.identifiant_1_autre && <span className="error-message">{errors.identifiant_1_autre}</span>}
						</>
					)}

					<label htmlFor="identifiant_2">{t('steps.support.id2')}</label>
					<select
						id="identifiant_2"
						name="identifiant_2"
						value={data.identifiant_2 || 'aucun'}
						onChange={onChange}
						disabled={!isSupportPhysiqueOui}
					>
						{['aucun', 'qrcode', 'clair', 'unique', 'serie', 'calypso', 'amc', 'autre'].map((v) => (
							<option key={v} value={v}>
								{t(`catalog.identifiant.${v}`)}
							</option>
						))}
					</select>
					{data.identifiant_2 === 'autre' && (
						<>
							<label htmlFor="identifiant_2_autre">{t('steps.support.id1prec')}</label>
							<input
								type="text"
								id="identifiant_2_autre"
								name="identifiant_2_autre"
								placeholder={t('steps.support.id1ph')}
								value={data.identifiant_2_autre || ''}
								onChange={onChange}
								disabled={!isSupportPhysiqueOui}
								className={isSupportPhysiqueOui && !data.identifiant_2_autre ? 'error' : ''}
							/>
							{errors.identifiant_2_autre && <span className="error-message">{errors.identifiant_2_autre}</span>}
						</>
					)}
				</>
			)}
		</div>
	);
}

export function StepDevices({ data = {}, onChange, errors = {}, setErrors }) {
	const { t } = useTranslation();
	const isPrestatairesOui = data.prestataires_equipes === 'oui';

	// Toujours s'assurer que type_lecture est un tableau
	const selected = Array.isArray(data.type_lecture)
		? data.type_lecture
		: [];

	const handleCheckboxChange = (e) => {
		const { value, checked } = e.target;
		let updated = [...selected];

		if (checked) {
			updated.push(value);
		} else {
			updated = updated.filter(item => item !== value);
		}

		// Simuler un event classique pour ton onChange parent
		onChange({
			target: {
				name: "type_lecture",
				value: updated
			}
		});
	};

	return (
		<div className="section">
			<div className="section-title">{t('steps.devices.title')}</div>

			<label htmlFor="prestataires_equipes">
				{t('steps.devices.presta')}
			</label>

			<select
				id="prestataires_equipes"
				name="prestataires_equipes"
				value={data.prestataires_equipes || 'non'}
				onChange={onChange}
			>
				<option value="oui">{t('app.common.yes')}</option>
				<option value="non">{t('app.common.no')}</option>
			</select>

			{isPrestatairesOui && (
				<>
					<label>{t('steps.devices.typeLecture')}</label>

					<div className="checkbox-group">
						<label>
							<input
								type="checkbox"
								value="QR Code"
								checked={selected.includes("QR Code")}
								onChange={handleCheckboxChange}
							/>
							&nbsp;{t('steps.devices.qr')}
						</label>

						<label>
							<input
								type="checkbox"
								value="NFC"
								checked={selected.includes("NFC")}
								onChange={handleCheckboxChange}
							/>
							&nbsp;NFC
						</label>
					</div>
					{errors.type_lecture && (
						<div className="error-message mt-1">{errors.type_lecture}</div>
					)}					
				</>
			)}
		</div>
	);
}

export function StepWebSite({ data = {}, onChange, onMultiSelect, errors = {} }) {
	const { t } = useTranslation();
	const isFrontOtipassOui = data.front_otipass === 'oui';
	const isFrontDomainOui = data.front_domain && data.front_domain !== '';
	const isFrontDomainBookingOui = data.front_domain_booking === 'oui';

	return (
		<div className="section">
			<div className="section-title">{t('steps.website.title')}</div>
			<label htmlFor="front_otipass">{t('steps.website.ordered')}</label>
			<select
				id="front_otipass"
				name="front_otipass"
				value={data.front_otipass || 'non'}
				onChange={onChange}
			>
				<option value="oui">{t('app.common.yes')}</option>
				<option value="non">{t('app.common.no')}</option>
			</select>
			{isFrontOtipassOui && (
				<>
					<label htmlFor="front_languages">{t('steps.website.langs')}</label>
					<select
						id="front_languages"
						name="front_languages"
						multiple
						value={data.front_languages || (isFrontOtipassOui ? ['Français', 'Anglais'] : [])}
						onChange={onMultiSelect}
						disabled={!isFrontOtipassOui}
					>
						{['Français', 'Anglais', 'Espagnol', 'Allemand', 'Italien', 'Néerlandais', 'Finnois', 'Basque', 'Catalan'].map((v) => (
							<option key={v} value={v}>
								{t(`catalog.frontLang.${v}`)}
							</option>
						))}
					</select>
				</>
			)}
			<label htmlFor="front_domain">{t('steps.website.domainQ')}</label>
			<input
				type="text"
				id="front_domain"
				name="front_domain"
				placeholder="Exemple : pass.beziers-mediterranee.com"
				value={data.front_domain || ''}
				onChange={onChange}
			/>
			{errors.front_domain && <span className="error-message">{errors.front_domain}</span>}
			{isFrontDomainOui && (
				<>
					<label htmlFor="front_domain_booking">{t('steps.website.bookQ')}</label>
					<select
						id="front_domain_booking"
						name="front_domain_booking"
						value={data.front_domain_booking || 'non'}
						onChange={onChange}
						disabled={!isFrontDomainOui}
					>
						<option value="oui">{t('app.common.yes')}</option>
						<option value="non">{t('app.common.no')}</option>
					</select>
					{isFrontDomainBookingOui && (
						<>
							<label htmlFor="front_domain_operator">{t('steps.website.operator')}</label>
							<input
								type="text"
								id="front_domain_operator"
								name="front_domain_operator"
								placeholder={t('steps.website.phOp')}
								value={data.front_domain_operator || ''}
								onChange={onChange}
								disabled={!isFrontDomainBookingOui}
								className={isFrontDomainBookingOui && !data.front_domain_operator ? 'error' : ''}
							/>
							{errors.front_domain_operator && <span className="error-message">{errors.front_domain_operator}</span>}
						</>
					)}
				</>
			)}
		</div>
	);
}

export function StepSocialNetwork({ data = {}, onChange, errors = {}, onBlur }) {
	const { t } = useTranslation();
	return (
	<div className="section">
		<div className="section-title">{t('steps.social.title')}</div>
		<label htmlFor="facebook_pass">{t('steps.social.fb')}</label>
		<input
			type="url"
			id="facebook_pass"
			name="facebook_pass"
			placeholder="Exemple : https://facebook.com/sampass"
			value={data.facebook_pass || ''}
			onChange={onChange}
			onBlur={onBlur}
			className={errors.facebook_pass ? 'error' : ''}
		/>
		{errors.facebook_pass && <span className="error-message">{errors.facebook_pass}</span>}
		<label htmlFor="youtube_pass">{t('steps.social.yt')}</label>
		<input
			type="url"
			id="youtube_pass"
			name="youtube_pass"
			placeholder="Exemple : https://www.youtube.com/user/sampass"
			value={data.youtube_pass || ''}
			onChange={onChange}
			onBlur={onBlur}
			className={errors.youtube_pass ? 'error' : ''}
		/>
		{errors.youtube_pass && <span className="error-message">{errors.youtube_pass}</span>}
		<label htmlFor="instagram_pass">{t('steps.social.ig')}</label>
		<input
			type="url"
			id="instagram_pass"
			name="instagram_pass"
			placeholder="Exemple : https://www.instagram.com/sam_pass/"
			value={data.instagram_pass || ''}
			onChange={onChange}
			onBlur={onBlur}
			className={errors.instagram_pass ? 'error' : ''}
		/>
		{errors.instagram_pass && <span className="error-message">{errors.instagram_pass}</span>}
		<label htmlFor="x_pass">{t('steps.social.x')}</label>
		<input
			type="url"
			id="x_pass"
			name="x_pass"
			placeholder="Exemple : https://x.com/SamPass"
			value={data.x_pass || ''}
			onChange={onChange}
			onBlur={onBlur}
			className={errors.x_pass ? 'error' : ''}
		/>
		{errors.x_pass && <span className="error-message">{errors.x_pass}</span>}
	</div>
	);
}

export function StepSubscriptions({ data = {}, onChange, errors = {}, setErrors, validateStep }) {
	const { t } = useTranslation();
	const userDataLabels = t('catalog.userData', { returnObjects: true });
	const setField = useCallback((name, value) => {
		onChange({ target: { name, value } });
	}, [onChange]);
	const toggleArrayValue = (name, value) => {
		const arr = Array.isArray(data[name]) ? [...data[name]] : [];
		const idx = arr.indexOf(value);
		if (idx === -1) arr.push(value);
		else arr.splice(idx, 1);
		setField(name, arr);
	};
	const setArray = (name, arr) => setField(name, arr);
	useEffect(() => {
		// Quand distrib_pass_gratuit activé, s'assurer d'avoir des champs initiaux
		if (data.distrib_pass_gratuit === "oui") {
			if (typeof data.formulaire_inscription === "undefined") setField("formulaire_inscription", "non");
			if (!Array.isArray(data.formulaire_otipass_features)) setArray("formulaire_otipass_features", []);
			if (!Array.isArray(data.formulaire_pj_types)) setArray("formulaire_pj_types", []);
		}
		// Quand distrib_pass_payant activé, initialiser boutique flags
		if (data.distrib_pass_payant === "oui") {
			if (typeof data.boutique_existante === "undefined") setField("boutique_existante", "non");
			if (typeof data.boutique_otipass === "undefined") setField("boutique_otipass", "non");
			if (!Array.isArray(data.boutique_otipass_features)) setArray("boutique_otipass_features", []);
			if (!Array.isArray(data.payment_gateways)) setArray("payment_gateways", []);
		}
		// initialiser user_data_fields structure si absent
		if (!data.user_data_fields || typeof data.user_data_fields !== "object") {
			// nom, prénom, email requis et non décochables
			const obj = {};
		Object.keys(USER_DATA_FIELDS).forEach((f) => {
			obj[f] = {
			visible: ["Nom", "Prénom", "Adresse email"].includes(f),
			required: ["Nom", "Prénom", "Adresse email"].includes(f),
			};
		});
			setField("user_data_fields", obj);
		}
		// initialise uid_fields (vide par défaut)
		if (!Array.isArray(data.uid_fields)) setField("uid_fields", []);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [data.distrib_pass_gratuit, data.distrib_pass_payant]);
	const showFormulaireSection = data.distrib_pass_gratuit === "oui" && data.formulaire_inscription === "oui";
	const showFormulaireOtipass = data.distrib_pass_gratuit === "oui" && data.formulaire_otipass === "oui";
	const showBoutiqueSection = data.distrib_pass_payant === "oui";
	const showBoutiqueOtipass = showBoutiqueSection && data.boutique_otipass === "oui";
	const showUserDataSection = showFormulaireOtipass || showBoutiqueOtipass;
	const handleFormulaireOtipassToggle = (e) => {
		setField("formulaire_otipass", e.target.value);
		if (e.target.value === "non") {
			setArray("formulaire_otipass_features", []);
			setArray("formulaire_pj_types", []);
		}
		validateStep?.(9);
	};
	const handlePJTypeToggle = (type) => toggleArrayValue("formulaire_pj_types", type);
	const handleFormulaireFeatureToggle = (feature) => toggleArrayValue("formulaire_otipass_features", feature);
	const handleBoutiqueFeatureToggle = (feature) => toggleArrayValue("boutique_otipass_features", feature);
	const handleGatewayToggle = (gateway) => toggleArrayValue("payment_gateways", gateway);
	const toggleUserFieldVisible = (field) => {
		const obj = { ...(data.user_data_fields || {}) };
		obj[field] = obj[field] || { visible: false, required: false };
		obj[field].visible = !obj[field].visible;
		// if user unchecks a default required field, ignore (Nom/Prénom/Adresse email must stay required & visible)
		if (["Nom", "Prénom", "Adresse email"].includes(field)) {
			obj[field].visible = true;
			obj[field].required = true;
		}
		setField("user_data_fields", obj);
	};
	const toggleUserFieldRequired = (field) => {
		if (["Nom", "Prénom", "Adresse email"].includes(field)) return; // cannot toggle required for forced fields
		const obj = { ...(data.user_data_fields || {}) };
		obj[field] = obj[field] || { visible: false, required: false };
		obj[field].required = !obj[field].required;
		// if required is set true ensure visible true
		if (obj[field].required) obj[field].visible = true;
		setField("user_data_fields", obj);
	};
	const toggleUidField = (field) => {
		const arr = Array.isArray(data.uid_fields) ? [...data.uid_fields] : [];
		const i = arr.indexOf(field);
		if (i === -1) arr.push(field);
		else arr.splice(i, 1);
		setField("uid_fields", arr);
	};
	const Checkbox = ({ checked, onChange, label, name, disabled }) => (
		<label style={{ display: "block", marginBottom: 0 }}>
			<input type="checkbox" checked={checked} onChange={onChange} disabled={disabled} />{" "}
			{label}
		</label>
	);

	return (
		<div className="section">
			<div className="section-title">{t('steps.subscriptions.title')}</div>

			{/* ----- PASS GRATUIT ----- */}
			<label>{t('steps.subscriptions.passGratuit')}</label>
			<select
				id="distrib_pass_gratuit"
				name="distrib_pass_gratuit"
				value={data.distrib_pass_gratuit || "non"}
				onChange={onChange}
			>
				<option value="oui">{t('app.common.yes')}</option>
				<option value="non">{t('app.common.no')}</option>
			</select>

			{/* If yes show inscription form block */}
			{data.distrib_pass_gratuit === "oui" && (
				<div className="card p-3 mb-3">
					<div style={{ fontWeight: 600, marginBottom: 8 }}>{t('steps.subscriptions.insBlock')}</div>
					<label>{t('steps.subscriptions.existingForm')}</label>
					<select
						id="formulaire_inscription"
						name="formulaire_inscription"
						value={data.formulaire_inscription || "non"}
						onChange={onChange}
					>
						<option value="oui">{t('app.common.yes')}</option>
						<option value="non">{t('app.common.no')}</option>
					</select>
					{showFormulaireSection && (
						<>
							<label htmlFor="url_formulaire">{t('steps.subscriptions.urlForm')}</label>
							<input
								type="url"
								id="url_formulaire"
								name="url_formulaire"
								placeholder="Exemple : https://pass.agglo.fr/inscription"
								value={data.url_formulaire || ""}
								onChange={(e) => setField("url_formulaire", e.target.value)}
								className={errors.url_formulaire ? "error" : ""}
							/>
							{errors.url_formulaire && <div className="error-message">{errors.url_formulaire}</div>}
						</>
					)}
					<div style={{ marginTop: 12 }}>
						<label>{t('steps.subscriptions.otipassForm')}</label>
						<select
							id="formulaire_otipass"
							name="formulaire_otipass"
							value={data.formulaire_otipass || "non"}
							onChange={handleFormulaireOtipassToggle}
						>
							<option value="oui">{t('app.common.yes')}</option>
							<option value="non">{t('app.common.no')}</option>
						</select>
					</div>
					{showFormulaireOtipass && (
						<div style={{ marginTop: 8 }}>
							<div style={{ fontWeight: 500 }}>{t('steps.subscriptions.otipassFeat')}</div>
							{FORMULAIRE_OTIPASS_FEATURES.map((f) => (

								<Checkbox
									key={f.value}
									checked={(data.formulaire_otipass_features || []).includes(f.value)}
									onChange={() => handleFormulaireFeatureToggle(f.value)}
									label={t(`catalog.featuresForm.${f.tKey}`)}
								/>
							))}
							{/* PJ types if selected */}
							{(data.formulaire_otipass_features || []).includes(FEATURE_FORM_PJ) && (
								<>
									<hr />
									<div className="fst-italic text-muted">{t('steps.subscriptions.pjTypes')}</div>
									{PJ_TYPES.map((pj) => (
										<Checkbox
											key={pj.value}
											checked={(data.formulaire_pj_types || []).includes(pj.value)}
											onChange={() => handlePJTypeToggle(pj.value)}
											label={t(`catalog.pj.${pj.tKey}`)}
										/>
									))}
									{ (data.formulaire_pj_types || []).includes("Autre") && (
										<input
											type="text"
											placeholder={t('steps.subscriptions.pjOtherPh')}
											value={data.formulaire_pj_types_other || ""}
											onChange={(e) => setField("formulaire_pj_types_other", e.target.value)}
										/>
									)}
								</>
							)}
						</div>
					)}
				</div>
			)}

			{/* ----- PASS PAYANT ----- */}
			<label>{t('steps.subscriptions.passPayant')}</label>
			<select
				id="distrib_pass_payant"
				name="distrib_pass_payant"
				value={data.distrib_pass_payant || "non"}
				onChange={onChange}
			>
				<option value="oui">{t('app.common.yes')}</option>
				<option value="non">{t('app.common.no')}</option>
			</select>

			{showBoutiqueSection && (
				<div className="card p-3 mb-3">
					<div style={{ fontWeight: 600, marginBottom: 8 }}>{t('steps.subscriptions.boutiqueBlock')}</div>
					<label>{t('steps.subscriptions.existingShop')}</label>
					<select
						id="boutique_existante"
						name="boutique_existante"
						value={data.boutique_existante || "non"}
						onChange={onChange}
					>
						<option value="oui">{t('app.common.yes')}</option>
						<option value="non">{t('app.common.no')}</option>
					</select>
					{data.boutique_existante === "oui" && (
						<>
							<label>{t('steps.subscriptions.editorShop')}</label>
							<input
								type="text"
								id="boutique_editeur"
								name="boutique_editeur"
								placeholder="Ex. Vivaticket"
								value={data.boutique_editeur || ""}
								onChange={(e) => setField("boutique_editeur", e.target.value)}
							/>
							<label>{t('steps.subscriptions.urlShop')}</label>
							<input
								type="url"
								id="url_boutique"
								name="url_boutique"
								placeholder="Ex. https://boutique.example.com"
								value={data.url_boutique || ""}
								onChange={(e) => setField("url_boutique", e.target.value)}
								className={errors.url_boutique ? "error" : ""}
							/>
							{errors.url_boutique && <div className="error-message">{errors.url_boutique}</div>}
						</>
					)}

					<div style={{ marginTop: 12 }}>
						<label>{t('steps.subscriptions.otipassShop')}</label>
						<select
							id="boutique_otipass"
							name="boutique_otipass"
							value={data.boutique_otipass || "non"}
							onChange={onChange}
						>
							<option value="oui">{t('app.common.yes')}</option>
							<option value="non">{t('app.common.no')}</option>
						</select>
					</div>

					{showBoutiqueOtipass && (
						<div style={{ marginTop: 8 }}>
							<div style={{ fontWeight: 500, marginBottom: 6 }}>{t('steps.subscriptions.otipassShopFeat')}</div>
							{BOUTIQUE_OTIPASS_FEATURES.map((f) => (
								<React.Fragment key={f.value}>
									<Checkbox
										checked={(data.boutique_otipass_features || []).includes(f.value)}
										onChange={() => handleBoutiqueFeatureToggle(f.value)}
										label={t(`catalog.featuresBoutique.${f.tKey}`)}
									/>
									{ f.value === FEATURE_BOUTIQUE_TARIF && (data.boutique_otipass_features || []).includes(f.value) && (
										<textarea
										placeholder={t('steps.subscriptions.conditions')}
										value={data.conditions_tarif_preferentiel || ""}
										onChange={(e) => setField("conditions_tarif_preferentiel", e.target.value)}
										/>
									)}
								</React.Fragment>
							))}
					{(data.boutique_otipass_features || []).includes(FEATURE_PAIEMENT_CB) && (
						<>
							<hr />
							<div className="fst-italic text-muted">{t('steps.subscriptions.paymentGws')}</div>
							<div style={{
								display: "grid", 
								gridTemplateColumns: "repeat(2, 1fr)", 
								rowGap: 0,
								columnGap: 12,
								marginTop: 4
							}}>				
								{PAYMENT_GATEWAYS.map((g) => (
									<Checkbox
										key={g.value}
										checked={(data.payment_gateways || []).includes(g.value)}
										onChange={() => handleGatewayToggle(g.value)}
										label={t(`catalog.payment.${g.tKey}`)}
									/>
								))}
							</div>
							{(data.payment_gateways || []).includes("Autre") && (
								<input
									type="text"
									placeholder={t('steps.subscriptions.gwOther')}
									value={data.payment_gateway_other || ""}
									onChange={(e) => setField("payment_gateway_other", e.target.value)}
								/>
							)}
						</>
					)}
				</div>
			)}
		</div>
			)}

			{/* ----- DONNÉES UTILISATEURS RÉCOLTÉES ----- */}
			{showUserDataSection && (
				<div className="card p-3 mb-3">
					<div style={{ fontWeight: 600, marginBottom: 8 }}>{t('steps.subscriptions.userData')}</div>

					<div style={{ marginBottom: 8 }}>
			{Object.keys(USER_DATA_FIELDS).map((f) => {
				const state = (data.user_data_fields && data.user_data_fields[f]) || { visible: false, required: false };
				return (
				<div key={f} style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 0 }}>
					<label style={{ minWidth: 220 }}>
					<input
						type="checkbox"
						checked={!!state.visible}
						onChange={() => toggleUserFieldVisible(f)}
						disabled={["Nom", "Prénom", "Adresse email"].includes(f)}
					/>{" "}
					{typeof userDataLabels === "object" && userDataLabels !== null && userDataLabels[f] != null
						? userDataLabels[f] : f}
					</label>
					<label style={{ fontStyle: "italic", fontSize: "90%" }}>
					<input
						type="checkbox"
						checked={!!state.required}
						onChange={() => toggleUserFieldRequired(f)}
						disabled={["Nom", "Prénom", "Adresse email"].includes(f)}
					/>{" "}
					{t('steps.subscriptions.obligatoire')}
					</label>
				</div>
				);
			})}
					</div>

					<div style={{ marginTop: 8 }}>
						<div style={{ fontWeight: 500 }}>{t('steps.subscriptions.uid')}</div>
			{Object.entries(USER_DATA_FIELDS)
				.filter(([_, info]) => info.UUID)
				.map(([f]) => (
				<label key={f} style={{ display: "inline-block", marginRight: 12 }}>
					<input
					type="checkbox"
					checked={Array.isArray(data.uid_fields) && data.uid_fields.includes(f)}
					onChange={() => toggleUidField(f)}
					/>{" "}
					{typeof userDataLabels === "object" && userDataLabels !== null && userDataLabels[f] != null
						? userDataLabels[f] : f}
				</label>
				))}
					</div>
				</div>
			)}

			{/* End */}
		</div>
	);
}

export function StepFinance({ data = {}, onChange, onMultiSelect, errors = {} }) {
	const { t } = useTranslation();
	const currencies = Array.isArray(data.finance_currencies_iso) ? data.finance_currencies_iso : ['EUR'];
	return (
		<div className="section">
			<div className="section-title">{t('steps.finance.title')}</div>
			<label htmlFor="finance_currencies_iso">{t('steps.finance.currencies')}</label>
			<select
				id="finance_currencies_iso"
				name="finance_currencies_iso"
				multiple
				value={currencies}
				onChange={onMultiSelect}
				className={errors.finance_currencies_iso ? 'error' : ''}
			>
				{FINANCE_CURRENCY_ISO_OPTIONS.map((o) => (
					<option key={o.value} value={o.value}>
						{t(`catalog.currency.${o.value}`)}
					</option>
				))}
			</select>
			{errors.finance_currencies_iso && (
				<span className="error-message">{errors.finance_currencies_iso}</span>
			)}
			<label htmlFor="finance_periodicity_entries" className="mt-3 d-block">
				{t('steps.finance.periodEntrees')}
			</label>
			<select
				id="finance_periodicity_entries"
				name="finance_periodicity_entries"
				value={String(data.finance_periodicity_entries ?? 2)}
				onChange={onChange}
			>
				{FINANCE_PERIODICITY_OPTIONS.map((o) => (
					<option key={o.value} value={String(o.value)}>
						{t(`catalog.financePeriodicity.${o.tKey}`)}
					</option>
				))}
			</select>
			<label htmlFor="finance_periodicity_sales" className="mt-3 d-block">
				{t('steps.finance.periodVentes')}
			</label>
			<select
				id="finance_periodicity_sales"
				name="finance_periodicity_sales"
				value={String(data.finance_periodicity_sales ?? 2)}
				onChange={onChange}
			>
				{FINANCE_PERIODICITY_OPTIONS.map((o) => (
					<option key={o.value} value={String(o.value)}>
						{t(`catalog.financePeriodicity.${o.tKey}`)}
					</option>
				))}
			</select>
		</div>
	);
}

export function StepVarious({ data = {}, onChange, onMultiSelect, errors = {} }) {
	const { t } = useTranslation();
	const [primaryColor, setPrimaryColor] = useState(data.primary_color || "");
	const [secondaryColor, setSecondaryColor] = useState(data.secondary_color || "");

	// Synchroniser si data change depuis l'extérieur
	useEffect(() => {
		setPrimaryColor(data.primary_color || "");
		setSecondaryColor(data.secondary_color || "");
	}, [data.primary_color, data.secondary_color]);

	const handleColorChange = (e) => {
		const val = e.target.value;
		setPrimaryColor(val);
		onChange({ target: { name: "primary_color", value: val } });
	};

	const handleTextChange = (e) => {
		let val = e.target.value;
		if (val && val[0] !== "#") val = "#" + val;
		setPrimaryColor(val);
		onChange({ target: { name: "primary_color", value: val } });
	};

	const handleSecondaryColorChange = (e) => {
		const val = e.target.value;
		setSecondaryColor(val);
		onChange({ target: { name: "secondary_color", value: val } });
	};

	const handleSecondaryTextChange = (e) => {
		let val = e.target.value;
		if (val && val[0] !== "#") val = "#" + val;
		setSecondaryColor(val);
		onChange({ target: { name: "secondary_color", value: val } });
	};

	return (
		<div className="section">
			<div className="section-title">{t('steps.various.title')}</div>

			<label htmlFor="langues_backoffice">{t('steps.various.backLangs')}</label>
			<select
				id="langues_backoffice"
				name="langues_backoffice"
				multiple
				value={data.langues_backoffice || ['Français', 'Anglais']}
				onChange={onMultiSelect}
			>
				{['Français', 'Anglais', 'Espagnol', 'Allemand', 'Italien', 'Néerlandais'].map((v) => (
					<option key={v} value={v}>
						{t(`catalog.frontLang.${v}`)}
					</option>
				))}
			</select>

			<label htmlFor="primary_color">{t('steps.various.primary')}</label>
			<div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
				<input type="text" value={primaryColor} onChange={handleTextChange} placeholder={t('steps.various.phColor')} />
				<input type="color" id="primary_color" name="primary_color" value={primaryColor || "#000000"} onChange={handleColorChange} />
			</div>

			<label htmlFor="secondary_color">{t('steps.various.secondary')}</label>
			<div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
				<input type="text" value={secondaryColor} onChange={handleSecondaryTextChange} placeholder={t('steps.various.phColor2')} />
				<input type="color" id="secondary_color" name="secondary_color" value={secondaryColor || "#000000"} onChange={handleSecondaryColorChange} />
			</div>

			<label htmlFor="application_mobile">{t('steps.various.mobile')}</label>
			<select id="application_mobile" name="application_mobile" value={data.application_mobile || 'non'} onChange={onChange}>
				<option value="oui">{t('app.common.yes')}</option>
				<option value="non">{t('app.common.no')}</option>
			</select>
		</div>
	);
}

export function StepContacts({ data = {}, onChange, onMultiSelect, errors = {} }) {
	const { t } = useTranslation();
	const [activeTab, setActiveTab] = useState("coordinateur");

	const contactTabs = useMemo(
		() => {
			const telField = (name, label) => ({
				name,
				label,
				mask: defaultTelMask,
				placeholder: t('catalog.placeholders.tel'),
			});

			const emailField = (name, label) => ({
				name,
				label,
				placeholder: t('catalog.placeholders.email'),
			});

			return [
			{
			key: "coordinateur",
			label: t('steps.contacts.tabCoord'),
			description: t('steps.contacts.tabCoordDesc'),
			fields: [
				{ name: "contact_coordinateur_lastname", label: t('steps.contacts.lastname') },
				{ name: "contact_coordinateur_firstname", label: t('steps.contacts.firstname') },
				emailField("contact_coordinateur_email", t('steps.contacts.email')),
				telField("contact_coordinateur_tel1", t('steps.contacts.tel1')),
				telField("contact_coordinateur_tel2", t('steps.contacts.tel2')),
			],
		},
		{
			key: "technique",
			label: t('steps.contacts.tabTech'),
			description: t('steps.contacts.tabTechDesc'),
			fields: [
				{ name: "contact_technique_lastname", label: t('steps.contacts.lastname') },
				{ name: "contact_technique_firstname", label: t('steps.contacts.firstname') },
				emailField("contact_technique_email", t('steps.contacts.email')),
				telField("contact_technique_tel1", t('steps.contacts.tel1')),
				telField("contact_technique_tel2", t('steps.contacts.tel2')),
			],
		},
		{
			key: "data",
			label: t('steps.contacts.tabData'),
			description: t('steps.contacts.tabDataDesc'),
			fields: [
				{ name: "contact_data_lastname", label: t('steps.contacts.lastname') },
				{ name: "contact_data_firstname", label: t('steps.contacts.firstname') },
				emailField("contact_data_email", t('steps.contacts.email')),
				telField("contact_data_tel1", t('steps.contacts.tel1')),
				telField("contact_data_tel2", t('steps.contacts.tel2')),
			],
		},
		{
			key: "compta",
			label: t('steps.contacts.tabCompta'),
			description: t('steps.contacts.tabComptaDesc'),
			fields: [
				{ name: "contact_compta_lastname", label: t('steps.contacts.lastname') },
				{ name: "contact_compta_firstname", label: t('steps.contacts.firstname') },
				emailField("contact_compta_email", t('steps.contacts.email')),
				telField("contact_compta_tel1", t('steps.contacts.tel1')),
				telField("contact_compta_tel2", t('steps.contacts.tel2')),
			],
		},
		{
			key: "autre",
			label: t('steps.contacts.tabAutre'),
			description: t('steps.contacts.tabAutreDesc'),
			fields: [
				{ name: "contact_other_function", label: t('steps.contacts.function') },
				{ name: "contact_other_lastname", label: t('steps.contacts.lastname') },
				{ name: "contact_other_firstname", label: t('steps.contacts.firstname') },
				emailField("contact_other_email", t('steps.contacts.email')),
				telField("contact_other_tel1", t('steps.contacts.tel1')),
				telField("contact_other_tel2", t('steps.contacts.tel2')),
			],
		},
		];
		},
		[t],
	);

	return (
		<div className="section">
			<div className="section-title">{t('steps.contacts.title')}</div>

			<ul className="nav nav-pills nav-fill mt-3" role="tablist">
				{contactTabs.map(tab => (
					<li key={tab.key} className="nav-item" role="presentation">
						<button
							className={`nav-link ${activeTab === tab.key ? "active" : ""}`}
							id={`${tab.key}-tab`}
							onClick={() => setActiveTab(tab.key)}
							type="button"
							role="tab"
						>
							{tab.label}
						</button>
					</li>
				))}
			</ul>

			<div className="tab-content">
				{contactTabs.map(tab => (
					<div
						key={tab.key}
						className={`tab-pane fade ${activeTab === tab.key ? "show active" : ""}`}
						id={tab.key}
						role="tabpanel"
					>
						<div className="tab-pane-content">
							<div className="form-text text-muted">{tab.description}</div>

							{tab.fields.map(field => (
								<div key={field.name} className="mb-3">
									<label htmlFor={field.name}>{field.label} :</label>
									{field.mask ? (
										<InputMask
											mask={field.mask}
											value={data[field.name] || ""}
											onChange={onChange}
										>
											{inputProps => <input type="tel" id={field.name} name={field.name} placeholder={field.placeholder} {...inputProps} />}
										</InputMask>
									) : (
										<input
											type="text"
											id={field.name}
											name={field.name}
											value={data[field.name] || ""}
						 placeholder={field.placeholder}
											onChange={onChange}
										/>
									)}
									{errors[field.name] && (
										<span className="error-message">{errors[field.name]}</span>
									)}
								</div>
							))}
						</div>
					</div>
				))}
			</div>
		</div>
	);
}

export function StepFormulas({ data = {}, onChange, errors = {}, setErrors }) {
	const { t } = useTranslation();

	const handleFormulaChange = (index, e) => {
		const { name, value } = e.target;
		const newFormulas = [...data.formules];
		newFormulas[index][name] = value;
		onChange({ target: { name: "formules", value: newFormulas } });
	};

	const addFormula = () => {
		const newFormulas = [
			...data.formules,
			champsFormula.reduce((acc, champ) => ({ ...acc, [champ.name]: "" }), {})
		];
		onChange({ target: { name: "formules", value: newFormulas } });
	};

	const removeFormula = (index) => {
		if (data.formules.length > 1) {
			const newFormulas = [...data.formules];
			newFormulas.splice(index, 1);
			onChange({ target: { name: "formules", value: newFormulas } });
		}
	};

	return (
		<div className="section">
			<div className="section-title">{t('steps.formulas.title')}</div>
			<div className="table-responsive rounded-table-wrapper">
				<table className="table table-sm table-borderless table-formules">
					<thead>
						<tr>
							{champsFormula.map((champ) => (
								<th key={champ.name} width={champ.colWidth}>
									<abbr title={t(`catalog.champsFormula.${champ.tKey}.title`)}>
										{t(`catalog.champsFormula.${champ.tKey}.label`)}
									</abbr>
								</th>
							))}
							<th></th>
						</tr>
					</thead>
					<tbody>
						{data.formules.map((formule, index) => (
							<React.Fragment key={index}>
								<tr>
									{champsFormula.map((champ) => (
										<td key={champ.name}>
											<input
												type={champ.type}
												name={champ.name}
												placeholder={
													['package', 'reference', 'duree', 'periode'].includes(champ.name)
														? t(`catalog.champsFormula.${champ.tKey}.ph`)
														: ''
												}
												value={formule[champ.name]}
												onChange={(e) => handleFormulaChange(index, e)}
												className={`formule-input ${champ.className || ""}`}
												min={champ.min}
												max={champ.max}
												step={champ.step}
												maxLength={champ.maxLength}
											/>
										</td>
									))}
									<td>
										<button
											type="button"
											onClick={() => removeFormula(index)}
											disabled={data.formules.length <= 1}
											className="remove-btn btn btn-sm btn-secondary"
										>
											<i className="bi bi-x"></i>
										</button>
									</td>
								</tr>
								<tr>
									<td colSpan={champsFormula.length + 1}>
										{errors.formules && errors.formules.includes(index) && (
											<span className="error-message">
												<big>{t('validation.formuleFields')}</big>
											</span>
										)}
									</td>
								</tr>
							</React.Fragment>
						))}
					</tbody>
					<tfoot>
						<tr>
							<td colSpan={champsFormula.length + 1}>
								<button
									type="button"
									onClick={addFormula}
									className="add-formule-btn btn btn-secondary btn-sm"
								>
									<i className="bi bi-plus-lg"></i> {t('steps.formulas.add')}
								</button>
							</td>
						</tr>
					</tfoot>
				</table>
			</div>
		</div>
	);
}

export function StepRecapitulative({ data, step, setStep }) {
	const { t } = useTranslation();
	const yesNo = (v) => (v === 'oui' ? t('app.common.yes') : v === 'non' ? t('app.common.no') : v ?? t('app.common.dash'));
	const trCountry = (country) => t(`catalog.countries.${country}`, { defaultValue: country });
	const userDataLabels = t('catalog.userData', { returnObjects: true });
	const trFormFeature = (v) => {
		const o = FORMULAIRE_OTIPASS_FEATURES.find((f) => f.value === v);
		return o ? t(`catalog.featuresForm.${o.tKey}`) : v;
	};
	const trBoutiqueFeature = (v) => {
		const o = BOUTIQUE_OTIPASS_FEATURES.find((f) => f.value === v);
		return o ? t(`catalog.featuresBoutique.${o.tKey}`) : v;
	};
	const trPj = (v) => {
		const o = PJ_TYPES.find((p) => p.value === v);
		return o ? t(`catalog.pj.${o.tKey}`) : v;
	};
	const trPayment = (v) => {
		const o = PAYMENT_GATEWAYS.find((g) => g.value === v);
		return o ? t(`catalog.payment.${o.tKey}`) : v;
	};

	const financeLabel = (v) => t(`catalog.financePeriodicity.${String(v)}`);

	const financeCurrenciesRecapText = (() => {
		const codes = data.finance_currencies_iso;
		if (!Array.isArray(codes) || codes.length === 0) {
			return t('app.common.dash');
		}
		return codes.map((c) => `${t(`catalog.currency.${c}`)} (${c})`).join(', ');
	})();

	const hasDataFormulas = data.formules && data.formules.some((formule) =>
		Object.values(formule).some((value) => value !== "")
	);
	const services = [
		data.service_dechetterie && t('steps.services.dechetterie'),
		data.service_piscine && t('steps.services.piscine'),
		data.service_mediatheque && t('steps.services.mediatheque'),
		data.service_musee && t('steps.services.musee'),
		data.service_transport && t('steps.services.transport'),
		data.service_autres && data.service_autres
	].filter(Boolean);

	return (
		<div className="section">
		<div className="section-subtitle text-muted">{t('recap.subtitle')}</div>
		<div className="recap-container">
			<div className="section-title d-flex justify-content-between align-items-center">
				{t('recap.sections.project')}
				{ step !== STEP_SUCCESS && (
					<button type="button" onClick={() => setStep(1, true)} className="btn btn-light btn-sm">
						<i className="bi bi-pencil-square"></i> {t('app.common.modify')}
					</button>
				)}
			</div>
			<p><strong>{t('recap.labels.platform')}</strong> {data.project_type === "ot" ? t('recap.labels.tourism') : t('recap.labels.pms')}</p>
			<p><strong>{t('recap.labels.nomProjet')}</strong> {data.nom_projet}</p>
			<p><strong>{t('recap.labels.nomPass')}</strong> {data.nom_pass}</p>
			<p><strong>{t('recap.labels.territoire')}</strong> {data.territoire}</p>
			<div className="section-title d-flex justify-content-between align-items-center">
				{t('recap.sections.issuer')}
				{ step !== STEP_SUCCESS && (
					<button type="button" onClick={() => setStep(2, true)} className="btn btn-light btn-sm">
						<i className="bi bi-pencil-square"></i> {t('app.common.modify')}
					</button>
				)}
			</div>
			<p><strong>{t('recap.labels.nomComplet')}</strong> {data.nom_emetteur}</p>
			<p><strong>{t('recap.labels.ref')}</strong> {data.reference_emetteur}</p>
			<p><strong>{t('recap.labels.adresse')}</strong> {data.adresse_postale}</p>
			<p><strong>{t('recap.labels.cp')}</strong> {data.code_postal}</p>
			<p><strong>{t('recap.labels.ville')}</strong> {data.ville}</p>
			<p><strong>{t('recap.labels.pays')}</strong> {data.pays === 'Autre' ? (data.pays_autre || t('app.common.dash')) : trCountry(data.pays)}</p>
			{data.telephone && <p><strong>{t('recap.labels.tel')}</strong> {data.telephone}</p>}
			<p><strong>{t('recap.labels.site')}</strong> {data.site_public}</p>
			<p><strong>{t('recap.labels.domaine')}</strong> {data.domaine}</p>
			<p><strong>{t('recap.labels.emailContact')}</strong> {data.email_public}</p>
			<p><strong>{t('recap.labels.emailNotif')}</strong> {data.email_reception}</p>
			{data.facebook && <p><strong>{t('recap.labels.fb')}</strong> {data.facebook}</p>}
			{data.youtube && <p><strong>{t('recap.labels.yt')}</strong> {data.youtube}</p>}
			{data.instagram && <p><strong>{t('recap.labels.ig')}</strong> {data.instagram}</p>}
			{data.x && <p><strong>{t('recap.labels.x')}</strong> {data.x}</p>}
			
			<div className="section-title d-flex justify-content-between align-items-center">
				{t('recap.sections.services')}
				{ step !== STEP_SUCCESS && (
					<button type="button" onClick={() => setStep(3, true)} className="btn btn-light btn-sm">
						<i className="bi bi-pencil-square"></i> {t('app.common.modify')}
					</button>
				)}
			</div>
			{data.project_type === 'pms' ? (
				<>
					<p><strong>{t('recap.labels.servSel')}</strong> {services.length > 0 ? services.join(", ") : t('app.common.none')}</p>			
				</>
			) : (
				<>
					<p><strong>{t('recap.labels.servOne')}</strong> {data.is_transport === "oui" ? t('steps.services.transport') : t('app.common.none')}</p>	
					{data.is_transport === "oui" && (
						<p>
							<strong>{t('recap.labels.operator')}</strong>
							{data.transport_operator === "Autre"
								? data.transport_operator_other || t('app.common.dash')
								: data.transport_operator ?? "Actoll"}
						</p>
					)}
					{data.is_transport === "oui" && (
						<p>
							<strong>{t('recap.labels.durDiff')}</strong>
							{data.is_transport_duration ?? t('app.common.no')}
						</p>
					)}
				</>
			)}
			<div className="section-title d-flex justify-content-between align-items-center">
				{t('recap.sections.tourist')}
				{ step !== STEP_SUCCESS && (
					<button type="button" onClick={() => setStep(4, true)} className="btn btn-light btn-sm">
						<i className="bi bi-pencil-square"></i> {t('app.common.modify')}
					</button>
				)}
			</div>
			<p><strong>{t('recap.labels.alu')}</strong> {data.alimentation_base_touristique ? yesNo(data.alimentation_base_touristique) : t('app.common.no')}</p>
			{data.alimentation_base_touristique === 'oui' ? (
				<>
					<p><strong>{t('recap.labels.platef')}</strong> {data.editeur_plateforme === 'Autre' ? data.editeur_plateforme_autre : data.editeur_plateforme}</p>
					<p><strong>{t('recap.labels.freq')}</strong> {data.frequence_synchronisations === 'Autre' ? data.frequence_synchronisations_autre : data.frequence_synchronisations}</p>
				</>
			) : null}
			<div className="section-title d-flex justify-content-between align-items-center">
				{t('recap.sections.visuel')}
				{ step !== STEP_SUCCESS && (
					<button type="button" onClick={() => setStep(5, true)} className="btn btn-light btn-sm">
						<i className="bi bi-pencil-square"></i> {t('app.common.modify')}
					</button>
				)}
			</div>
			{data.visuel_pass_recto && (
				<div className="recap-image-container">
				<p><strong>{t('recap.labels.visuel')}</strong></p>
				<img
					src={data.visuel_pass_recto}
					alt={t('steps.support.altVisuel')}
					className="recap-image-preview"
				/>
				</div>
			)}		
			<p><strong>{t('recap.labels.supDem')}</strong> {yesNo(data.support_dematerialise)}</p>
			<p><strong>{t('recap.labels.supPhys')}</strong> {yesNo(data.support_physique)}</p>

			{data.support_physique === 'oui' ? (
				<>
					{data.identifiant_1 && (
					<p><strong>{t('recap.labels.recto')}</strong>{' '}
						{data.identifiant_1 === 'aucun' && <span>{t('recap.labels.aucun')}</span>}
						{data.identifiant_1 === 'qrcode' && <span>{t('catalog.identifiant.qrcode')}</span>}
						{data.identifiant_1 === 'clair' && <span>{t('catalog.identifiant.clair')}</span>}
						{data.identifiant_1 === 'unique' && <span>{t('catalog.identifiant.unique')}</span>}
						{data.identifiant_1 === 'serie' && <span>{t('catalog.identifiant.serie')}</span>}
						{data.identifiant_1 === 'calypso' && <span>{t('catalog.identifiant.calypso')}</span>}
						{data.identifiant_1 === 'amc' && <span>{t('catalog.identifiant.amc')}</span>}
						{data.identifiant_1 === 'autre' && <span>{data.identifiant_1_autre}</span>}
					</p>
					)}
					{data.identifiant_2 && (
					<p><strong>{t('recap.labels.verso')}</strong>{' '}
						{data.identifiant_2 === 'aucun' && <span>{t('recap.labels.aucun')}</span>}
						{data.identifiant_2 === 'qrcode' && <span>{t('catalog.identifiant.qrcode')}</span>}
						{data.identifiant_2 === 'clair' && <span>{t('catalog.identifiant.clair')}</span>}
						{data.identifiant_2 === 'unique' && <span>{t('catalog.identifiant.unique')}</span>}
						{data.identifiant_2 === 'serie' && <span>{t('catalog.identifiant.serie')}</span>}
						{data.identifiant_2 === 'calypso' && <span>{t('catalog.identifiant.calypso')}</span>}
						{data.identifiant_2 === 'amc' && <span>{t('catalog.identifiant.amc')}</span>}
						{data.identifiant_2 === 'autre' && <span>{data.identifiant_2_autre}</span>}
					</p>
					)}
				</>
			) : null}
			
			<div className="section-title d-flex justify-content-between align-items-center">
				{t('recap.sections.devices')}
				{ step !== STEP_SUCCESS && (
					<button type="button" onClick={() => setStep(6, true)} className="btn btn-light btn-sm">
						<i className="bi bi-pencil-square"></i> {t('app.common.modify')}
					</button>
				)}
			</div>
			<p><strong>{t('recap.labels.presta')}</strong> {data.prestataires_equipes ? data.prestataires_equipes : t('app.common.no')}</p>
			{data.prestataires_equipes === 'oui' && (
				<p>
					<strong>{t('recap.labels.lect')}</strong>{" "}
					{Array.isArray(data.type_lecture) ? data.type_lecture.join(", ") : data.type_lecture}
				</p>
			)}
			
			
			<div className="section-title d-flex justify-content-between align-items-center">
				{t('recap.sections.website')}
				{ step !== STEP_SUCCESS && (
					<button type="button" onClick={() => setStep(7, true)} className="btn btn-light btn-sm">
						<i className="bi bi-pencil-square"></i> {t('app.common.modify')}
					</button>
				)}
			</div>
			<p><strong>{t('recap.labels.front')}</strong> {data.front_otipass ? data.front_otipass : t('app.common.no')}</p>
			{data.front_otipass === 'oui' && <p><strong>{data.front_languages && data.front_languages.length > 1 ? t('recap.labels.multilang') : t('recap.labels.langue')}</strong> {data.front_languages ? data.front_languages.map((l) => t(`catalog.frontLang.${l}`)).join(', ') : ''}</p>}
			{data.front_domain && <p><strong>{t('recap.labels.ndd')}</strong> {data.front_domain}</p>}
			{data.front_domain_operator && <p><strong>{t('recap.labels.booked')}</strong> {data.front_domain_operator}</p>}

			<div className="section-title d-flex justify-content-between align-items-center">
				{t('recap.sections.social')}
				{ step !== STEP_SUCCESS && (
					<button type="button" onClick={() => setStep(8, true)} className="btn btn-light btn-sm">
						<i className="bi bi-pencil-square"></i> {t('app.common.modify')}
					</button>
				)}
			</div>
			<p><strong>{t('recap.labels.fbPass')}</strong> {data.facebook_pass ? data.facebook_pass : t('recap.labels.non')}</p>
			<p><strong>{t('recap.labels.ytPass')}</strong> {data.youtube_pass ? data.youtube_pass : t('recap.labels.non')}</p>
			<p><strong>{t('recap.labels.igPass')}</strong> {data.instagram_pass ? data.instagram_pass : t('recap.labels.non')}</p>
			<p><strong>{t('recap.labels.xPass')}</strong> {data.x_pass ? data.x_pass : t('recap.labels.non')}</p>
			
			{/*Inscriptions & Boutique en ligne*/}
			<div className="section-title d-flex justify-content-between align-items-center">
				{t('recap.sections.inscr')}
				{ step !== STEP_SUCCESS && (
					<button type="button" onClick={() => setStep(9, true)} className="btn btn-light btn-sm">
						<i className="bi bi-pencil-square"></i> {t('app.common.modify')}
					</button>
				)}
			</div>
			<p><strong>{t('recap.labels.existingForm')}</strong> {data.formulaire_inscription ? (data.formulaire_inscription === 'oui' ? t('app.common.yes') : t('app.common.no')) : t('app.common.no')}</p>
			{data.formulaire_inscription === 'oui' ? <p><strong>{t('recap.labels.url')}</strong> {data.url_formulaire}</p> : null}
			{ data.formulaire_otipass === 'oui' && (
				<>
					<div className="subsection-title">{t('recap.labels.formatrix')}</div>
					{Array.isArray(data.formulaire_otipass_features) && (
						<ul>
							{Array.isArray(data.formulaire_otipass_features) && data.formulaire_otipass_features.map((feature, index) => (
								<li key={`${feature}-${index}`}>
									{trFormFeature(feature)}
									{feature === FEATURE_FORM_PJ && Array.isArray(data.formulaire_pj_types) && (
										<ul>
											{data.formulaire_pj_types.map((pj, idx) => (
												<li key={idx}>
													{trPj(pj)}
													{pj === 'Autre' && data.formulaire_pj_types_other
														? ` : ${data.formulaire_pj_types_other}`
														: null}
												</li>
											))}
										</ul>
									)}
								</li>
							))}
						</ul>
					)}
				</>
			)}
			<p>
				<strong>{t('recap.labels.existing')}</strong>{" "}
				{data.boutique_existante ? (
				<>
					{data.boutique_existante === 'oui' ? t('app.common.yes') : data.boutique_existante === 'non' ? t('app.common.no') : data.boutique_existante}
					{data.boutique_editeur ? ` (${data.boutique_editeur})` : ''}
					{data.boutique_existante === 'oui' ? <p><strong>{t('recap.labels.url')}</strong> {data.url_boutique}</p> : null}
				</>
				) : (
					t('app.common.no')
				)}
			</p>
			{ data.boutique_otipass === 'oui' && (
				<>
					<div className="subsection-title">{t('recap.labels.boutique')}</div>
					{Array.isArray(data.boutique_otipass_features) && (
						<ul>
							{Array.isArray(data.boutique_otipass_features) && data.boutique_otipass_features.map((feature, index) => (
								<li key={`boutique-${feature}-${index}`}>
									{trBoutiqueFeature(feature)}
									{feature === FEATURE_BOUTIQUE_TARIF &&
										(data.conditions_tarif_preferentiel || "").trim() !== "" && (
											<div
												className="mt-1 small text-body-secondary"
												style={{ whiteSpace: "pre-wrap" }}
											>
												{data.conditions_tarif_preferentiel}
											</div>
										)}
									{feature === FEATURE_PAIEMENT_CB && Array.isArray(data.payment_gateways) && (
										<ul>
											{data.payment_gateways.map((g, idx) => (
												<li key={idx}>{trPayment(g)}</li>
											))}
										</ul>
									)}
								</li>
							))}
						</ul>
					)}
				</>
			)}
			{data.user_data_fields && (
				<>
				<div className="subsection-title">{t('recap.labels.users')}</div>
				<ul>
					{Object.entries(data.user_data_fields)
					.filter(([, info]) => info.visible)
					.map(([field, info]) => (
						<li key={field}>
						{userDataLabels[field] || field}{info.required ? " *" : ""}
						</li>
					))
					}
				</ul>
				</>
			)}
			{Array.isArray(data.uid_fields) && data.uid_fields.length > 0 && (
				<>
				<div className="subsection-title">{t('recap.labels.uid')}</div>
				<ul>
					{data.uid_fields.map((field) => (
					<li key={field}>{userDataLabels[field] || field}</li>
					))}
				</ul>
				</>
			)}

			<div className="section-title d-flex justify-content-between align-items-center">
				{t('recap.sections.finance')}
				{step !== STEP_SUCCESS && (
					<button type="button" onClick={() => setStep(10, true)} className="btn btn-light btn-sm">
						<i className="bi bi-pencil-square"></i> {t('app.common.modify')}
					</button>
				)}
			</div>
			<p>
				<strong>{t('recap.labels.devises')}</strong> {financeCurrenciesRecapText}
			</p>
			<p>
				<strong>{t('recap.labels.periodE')}</strong>{' '}
				{financeLabel(data.finance_periodicity_entries)}
			</p>
			<p>
				<strong>{t('recap.labels.periodV')}</strong>{' '}
				{financeLabel(data.finance_periodicity_sales)}
			</p>

			<div className="section-title d-flex justify-content-between align-items-center">
				{t('recap.sections.various')}
				{ step !== STEP_SUCCESS && (
					<button type="button" onClick={() => setStep(11, true)} className="btn btn-light btn-sm">
						<i className="bi bi-pencil-square"></i> {t('app.common.modify')}
					</button>
				)}
			</div>
			<p><strong>{t('recap.labels.languesBo')}</strong> {data.langues_backoffice ? data.langues_backoffice.map((l) => t(`catalog.frontLang.${l}`)).join(', ') : t('recap.labels.non4')}</p>
			<p>
				<strong>{t('recap.labels.primary')}</strong>{' '}
				{data.primary_color ? (
					<>
					{' '}<i className="bi bi-square-fill" style={{ color: data.primary_color }}></i> {data.primary_color} 
					</>
				) : t('recap.labels.non5')}
			</p>
			<p>
				<strong>{t('recap.labels.secondary')}</strong>{' '}
				{data.secondary_color ? (
					<>
					{' '}<i className="bi bi-square-fill" style={{ color: data.secondary_color }}></i> {data.secondary_color} 
					</>
				) : t('recap.labels.non5')}
			</p>
			<p><strong>{t('recap.labels.mobile')}</strong> {data.application_mobile ? data.application_mobile : t('app.common.no')}</p>
			<div className="section-title d-flex justify-content-between align-items-center">
				{t('recap.sections.contacts')}
				{ step !== STEP_SUCCESS && (
					<button type="button" onClick={() => setStep(12, true)} className="btn btn-light btn-sm">
						<i className="bi bi-pencil-square"></i> {t('app.common.modify')}
					</button>
				)}
			</div>
			{
				(data.contact_coordinateur_lastname && data.contact_coordinateur_firstname) 
				|| (data.contact_technique_lastname && data.contact_technique_firstname) 
				|| (data.contact_data_lastname && data.contact_data_firstname) 
				|| (data.contact_compta_lastname && data.contact_compta_firstname) 
				|| (data.contact_other_function && data.contact_other_lastname && data.contact_other_firstname) 
			? (
				<>
					{data.contact_coordinateur_lastname && data.contact_coordinateur_firstname ? (
						<>
							<p className="recap-contact">{data.contact_coordinateur_lastname} {data.contact_coordinateur_firstname}</p>
							<p>
								<small className="text-muted">{t('recap.contact.coordinator')}</small>
								{data.contact_coordinateur_email && <><br /><strong className="text-muted"><i className="bi bi-envelope"></i> {data.contact_coordinateur_email}</strong></>}
								{(data.contact_coordinateur_tel1 || data.contact_coordinateur_tel2) && (
									<>
									<br />
									<strong className="text-muted">
										<i className="bi bi-telephone"></i>{' '}
										{data.contact_coordinateur_tel1}
										{data.contact_coordinateur_tel1 && data.contact_coordinateur_tel2 && '\u00A0\u00A0\u00A0\u00A0'}
										{data.contact_coordinateur_tel2}
									</strong>
									</>
								)}
							</p>
						</>
					) : null }
					{data.contact_technique_lastname && data.contact_technique_firstname ? (
						<>
							<p className="recap-contact">{data.contact_technique_lastname} {data.contact_technique_firstname}</p>
							<p>
							<small className="text-muted">{t('recap.contact.technical')}</small>
								{data.contact_technique_email && <><br /><strong className="text-muted"><i className="bi bi-envelope"></i> {data.contact_technique_email}</strong></>}
								{(data.contact_technique_tel1 || data.contact_technique_tel2) && (
									<>
									<br />
									<strong className="text-muted">
										<i className="bi bi-telephone"></i>{' '}
										{data.contact_technique_tel1}
										{data.contact_technique_tel1 && data.contact_technique_tel2 && '\u00A0\u00A0\u00A0\u00A0'}
										{data.contact_technique_tel2}
									</strong>
									</>
								)}
							</p>
						</>
					) : null }
					{data.contact_data_lastname && data.contact_data_firstname ? (
						<>
							<p className="recap-contact">{data.contact_data_lastname} {data.contact_data_firstname}</p>
							<p>
								<small className="text-muted">{t('recap.contact.data')}</small>
								{data.contact_data_email && <><br /><strong className="text-muted"><i className="bi bi-envelope"></i> {data.contact_data_email}</strong></>}
								{(data.contact_data_tel1 || data.contact_data_tel2) && (
									<>
									<br />
									<strong className="text-muted">
										<i className="bi bi-telephone"></i>{' '}
										{data.contact_data_tel1}
										{data.contact_data_tel1 && data.contact_data_tel2 && '\u00A0\u00A0\u00A0\u00A0'}
										{data.contact_data_tel2}
									</strong>
									</>
								)}
							</p>
						</>
					) : null }
					{data.contact_compta_lastname && data.contact_compta_firstname ? (
						<>
							<p className="recap-contact">{data.contact_compta_lastname} {data.contact_compta_firstname}</p>
							<p>
								<small className="text-muted">{t('recap.contact.accounting')}</small>
								{data.contact_compta_email && <><br /><strong className="text-muted"><i className="bi bi-envelope"></i> {data.contact_compta_email}</strong></>}
								{(data.contact_compta_tel1 || data.contact_compta_tel2) && (
									<>
									<br />
									<strong className="text-muted">
										<i className="bi bi-telephone"></i>{' '}
										{data.contact_compta_tel1}
										{data.contact_compta_tel1 && data.contact_compta_tel2 && '\u00A0\u00A0\u00A0\u00A0'}
										{data.contact_compta_tel2}
									</strong>
									</>
								)}
							</p>
						</>
					) : null }
					{data.contact_other_function && data.contact_other_lastname && data.contact_other_firstname ? (
						<>
							<p className="recap-contact">{data.contact_other_lastname} {data.contact_other_firstname}</p>
							<p>
								<small className="text-muted">{data.contact_other_function}</small>
								{data.contact_other_email && <><br /><strong className="text-muted"><i className="bi bi-envelope"></i> {data.contact_other_email}</strong></>}
								{(data.contact_other_tel1 || data.contact_other_tel2) && (
									<>
									<br />
									<strong className="text-muted">
										<i className="bi bi-telephone"></i>{' '}
										{data.contact_other_tel1}
										{data.contact_other_tel1 && data.contact_other_tel2 && '\u00A0\u00A0\u00A0\u00A0'}
										{data.contact_other_tel2}
									</strong>
									</>
								)}
							</p>
						</>
					) : null }
				</>
			) : <i className="text-muted">{t('recap.labels.aucunC')}</i> }
			<div className="section-title d-flex justify-content-between align-items-center">
				{t('recap.sections.formules')}
				{ step !== STEP_SUCCESS && (
					<button type="button" onClick={() => setStep(13, true)} className="btn btn-light btn-sm">
						<i className="bi bi-pencil-square"></i> {t('app.common.modify')}
					</button>
				)}
			</div>
			{hasDataFormulas ? (
				<>
					{data.formules.map((formule, index) => (
						<React.Fragment key={index}>
							<p className="recap-formule">
								{formule.package}
								<strong>
									&nbsp;(<span className="uppercase">{formule.reference}</span>)
									<br />
									{t('recap.labels.usable', { d: formule.duree, p: formule.periode })}
									{formule.tarifAdulte && ( <><br />{t('recap.labels.tarifA')} {formule.tarifAdulte} {t('recap.labels.euro')}</> )}
									{formule.tarifEnfant && ( <><br />{t('recap.labels.tarifE')} {formule.tarifEnfant} {t('recap.labels.euro')}</> )}
									{formule.accompagnants && ( <><br />{t('recap.labels.acc')} {formule.accompagnants}</> )}
								</strong>
							</p>
						</React.Fragment>
					))}
				</>
			) : <i className="text-muted">{t('recap.labels.non6')}</i>}
			<div className="mb-5"></div>
		</div>
		</div>
	);
}

export function StepSuccess({ data }) {
	const { t } = useTranslation();
	return (
		<div className="section">
			<div className="success-container">
				<p>{t('recap.success.thanks')}</p>
				<h2>{t('recap.success.submitted')}</h2>
				<p>{t('recap.success.saved')}</p>
				<p>{t('recap.success.printHint')}</p>
				<button type="button" onClick={() => window.print()} className="print-button">
					{t('recap.success.printBtn')}
				</button>
			</div>
			<div className="recap-container">
				<StepRecapitulative data={data} step={STEP_SUCCESS} setStep={() => {}} />
			</div>
		</div>
	);
}
